import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { Card } from '../../../../molecules/card/card';
import { Button, StyledDiv } from '../../../../atoms/button/button';
import {
  Typography,
  Spacing,
} from '../../../../atoms/progress-with-label/progress-with-label';
import { StyledFixedButtonWrapper } from '../../../../common/components/styled-fixed-button-wrapper/styled-fixed-button-wrapper';
import { AudioSyncStatusBadge } from '../../../../molecules/audio-sync-status-badge/audio-sync-status-badge';
import { Skeleton } from '../../../../atoms/skeleton/skeleton';
import { IObservation, ObservationStatus } from '@taleemabad/db';
import { FC } from 'react';

// Minimal shape for list item enriched for UI without leaking backend-specific snake_case.
export interface TeachingReviewListItem {
  id: string;
  date: string;
  grade?: string | null;
  section?: string | null;
  className: string | null;
  subject: string | null;
  lesson: string | null;
  feedback?: string | null;
  isRead?: boolean;
  isSelfObservation?: boolean;
  hasAudio?: boolean;
  audioSyncStatus?: 'pending' | 'syncing' | 'synced' | 'failed' | 'recording';
  uploadProgress?: number;
  audioUrl?: string | null;
  observation?: Partial<IObservation>;
  status?: ObservationStatus;
  // Optional: shown only when defined; null renders as "—"
  coachName?: string | null;
  // Optional: status text shown below coach row; missing renders nothing.
  statusText?: string | null;
  // Optional: when explicitly false, the "View Insights" button is not rendered.
  // Default behaviour (when omitted) keeps existing rendering — button is shown.
  showViewInsights?: boolean;
}

export interface TeachingReviewListSection {
  title: string;
  observations: TeachingReviewListItem[];
}

export interface TeachingReviewListProps {
  teacherName: string;
  observations?: TeachingReviewListItem[];
  sections?: TeachingReviewListSection[];
  onViewFeedback: (id: string) => void;
  onStartReview: () => void;
  isStartReviewEnabled?: boolean;
  onRetryUpload?: (id: string) => void;
  isOnline?: boolean;
  isLoading?: boolean;
  startReviewLabel?: string;
  uploadProgressMap?: Record<
    string,
    { progress: number; step?: string; details?: string }
  >;
  visitedId?: string | null;
  visitedItemRef?: (element: HTMLElement | null) => void;
}

export const TeachingReviewList: FC<TeachingReviewListProps> = ({
  teacherName,
  observations = [],
  sections,
  onViewFeedback,
  onStartReview,
  isStartReviewEnabled = false,
  onRetryUpload,
  isOnline = true,
  isLoading = false,
  startReviewLabel = 'Start Class Insight',
  uploadProgressMap,
  visitedId,
  visitedItemRef,
}) => {
  const displaySections = sections ?? [
    { title: '', observations: observations },
  ];
  const hasAny = displaySections.some((s) => s.observations.length > 0);

  const sanitizeDisplayValue = (
    value: string | null | undefined,
    disallowed: string[] = [],
  ) => {
    if (!value) return '-';
    const trimmed = value.trim();
    if (!trimmed) return '-';
    const lower = trimmed.toLowerCase();
    if (lower === 'n/a' || lower === 'na' || disallowed.includes(lower)) {
      return '-';
    }
    return trimmed;
  };

  return (
    <StyledDiv
      flexDirection="column"
      width="100%"
      padding="0 0 var(--rk-spacing-xxxxxxxxxl) 0"
    >
      {!hasAny && !isLoading && (
        <StyledEmptyStateContainer>
          <Typography variant="textBodyMedium" textAlign="center">
            No class insights available yet.
          </Typography>
          <Spacing marginTop="md" />
          <Typography variant="textBodySmall" textAlign="center">
            You will see your class insights once they are recorded or submitted
            by a coach.
          </Typography>
        </StyledEmptyStateContainer>
      )}

      {isLoading && (
        <StyledCardsContainer>
          {[1, 2, 3].map((i) => (
            <Card key={i} variant="outline" minHeight="60">
              <StyledDiv flexDirection="column" gap={4}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="80%" height={20} />
                <Skeleton variant="text" width="40%" height={16} />
              </StyledDiv>
              <Spacing marginTop="lg" />
              <Skeleton variant="rectangular" height={40} />
              <Spacing marginTop="sm" />
              <StyledDiv justifyContent="space-between" alignItem="center">
                <Skeleton variant="text" width="40%" height={16} />
                <Skeleton variant="circular" width={20} height={20} />
              </StyledDiv>
            </Card>
          ))}
        </StyledCardsContainer>
      )}

      {hasAny &&
        displaySections.map((section, sIdx) => {
          if (section.observations.length === 0) return null;

          return (
            <StyledDiv key={sIdx} flexDirection="column" width="100%">
              {section.title && (
                <>
                  <Spacing marginTop="lg" />
                  <Typography variant="textTitleMedium">
                    {section.title}
                  </Typography>
                  <Spacing marginTop="sm" />
                </>
              )}
              <StyledCardsContainer>
                {section.observations.map((o) => {
                  const hasFeedback =
                    !!o.feedback && o.feedback.trim().length > 0;
                  let footerText = '';
                  const hasRemoteAudio = !!o.audioUrl;
                  if (!hasFeedback) {
                    if (o.isSelfObservation) {
                      if (o.hasAudio) {
                        switch (o.audioSyncStatus) {
                          case 'pending':
                            footerText =
                              o.status === ObservationStatus.InProgress
                                ? 'Waiting to upload'
                                : 'Processing recording';
                            break;
                          case 'recording':
                            footerText = 'Recording in progress';
                            break;
                          case 'syncing':
                            footerText = '';
                            break;
                          case 'synced':
                            footerText = 'Processing';
                            break;
                          case 'failed':
                            footerText = 'Upload failed';
                            break;
                          default:
                            footerText = 'Not submitted';
                        }
                      } else {
                        footerText = 'Not recorded';
                      }
                    } else {
                      footerText = 'Not submitted';
                    }
                  }
                  const classLabel = sanitizeDisplayValue(
                    o.grade || o.className,
                  );
                  const subjectLabel = sanitizeDisplayValue(o.subject);
                  const lessonLabel = sanitizeDisplayValue(o.lesson, [
                    'observation',
                  ]);
                  return (
                    <StyledOptimizedCardWrapper
                      key={o.id}
                      ref={visitedId === o.id ? visitedItemRef : undefined}
                    >
                      <Card variant="outline" minHeight="60">
                        <StyledDiv flexDirection="column" gap={4}>
                          {o.date && (
                            <StyledSecondaryText variant="textTitleMedium">
                              Insight for {o.date}
                            </StyledSecondaryText>
                          )}

                          <Typography variant="textTitleMedium">
                            {classLabel} - {subjectLabel}
                          </Typography>
                          {o.section && (
                            <Typography variant="textBodyMedium">
                              Section: {o.section}
                            </Typography>
                          )}
                          <Typography variant="textBodyMedium">
                            Lesson: {lessonLabel}
                          </Typography>
                          {'coachName' in o && o.coachName && (
                            <Typography variant="textBodyMedium">
                              Coach: {o.coachName}
                            </Typography>
                          )}
                          {'statusText' in o && o.statusText && (
                            <Typography variant="textBodyMedium">
                              {o.statusText}
                            </Typography>
                          )}
                        </StyledDiv>
                        <Spacing marginTop="lg" />
                        {o.showViewInsights !== false && (
                          <>
                            <Button
                              variant="outlined"
                              width="100%"
                              disabled={!hasFeedback && !o.isSelfObservation}
                              onClick={() => onViewFeedback(o.id)}
                            >
                              {hasFeedback
                                ? 'View Insights'
                                : o.isSelfObservation &&
                                    o.audioSyncStatus === 'synced'
                                  ? 'View Insights'
                                  : o.isSelfObservation &&
                                      o.status === ObservationStatus.InProgress
                                    ? 'Continue Insight'
                                    : o.isSelfObservation
                                      ? 'View Progress'
                                      : 'View Insights'}
                            </Button>
                            {!hasFeedback && (
                              <>
                                <Spacing marginTop="sm" />
                                <StyledFooterRow>
                                  <Typography variant="textBodySmallOnSurfaceVariant">
                                    {footerText}
                                    {(o.audioSyncStatus === 'recording' ||
                                      o.audioSyncStatus === 'syncing' ||
                                      footerText.includes('Processing')) && (
                                      <StyledAnimatedDots>
                                        <span>.</span>
                                        <span>.</span>
                                        <span>.</span>
                                      </StyledAnimatedDots>
                                    )}
                                  </Typography>
                                  {o.isSelfObservation &&
                                    o.hasAudio &&
                                    o.audioSyncStatus && (
                                      <StyledStatusWrapper>
                                        <AudioSyncStatusBadge
                                          status={o.audioSyncStatus as any}
                                          compact={
                                            o.audioSyncStatus !== 'syncing'
                                          }
                                          progressDetails={
                                            uploadProgressMap?.[o.id]?.details
                                          }
                                        />
                                        {(o.audioSyncStatus === 'failed' ||
                                          o.audioSyncStatus === 'pending') &&
                                          onRetryUpload && (
                                            <Button
                                              variant="text"
                                              onClick={() =>
                                                onRetryUpload(o.id)
                                              }
                                            >
                                              Retry
                                            </Button>
                                          )}
                                      </StyledStatusWrapper>
                                    )}
                                </StyledFooterRow>
                              </>
                            )}
                          </>
                        )}
                      </Card>
                    </StyledOptimizedCardWrapper>
                  );
                })}
              </StyledCardsContainer>
            </StyledDiv>
          );
        })}

      {isStartReviewEnabled && (
        <StyledReviewFixedButtonWrapper>
          <Button onClick={onStartReview} icon="add">
            {startReviewLabel}
          </Button>
        </StyledReviewFixedButtonWrapper>
      )}
    </StyledDiv>
  );
};

const StyledReviewFixedButtonWrapper = styled(StyledFixedButtonWrapper)`
  ${mq(BreakpointSizes.desktop)} {
    left: 272px;
  }
`;

const StyledSecondaryText = styled(Typography)`
  color: var(--md-sys-color-secondary);
`;

const StyledStatusWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: var(--rk-spacing-md);
  position: relative;
  z-index: 5;
`;

const StyledFooterRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--rk-spacing-xs) var(--rk-spacing-sm);
  min-height: 40px;
`;

const StyledEmptyStateContainer = styled(StyledDiv)`
  width: 100%;
  flex-direction: column;
  align-items: center;
  padding: var(--rk-spacing-xxxxl) 0;
`;

const StyledCardsContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--rk-spacing-lg);

  ${mq(BreakpointSizes.desktop)} {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }
`;

const dotAnimation = keyframes`
  0%, 20% { opacity: 0; }
  50% { opacity: 1; }
  100% { opacity: 0; }
`;

const StyledAnimatedDots = styled.span`
  display: inline-flex;
  margin-left: 2px;
  gap: 1px;

  span {
    animation: ${dotAnimation} 1.4s infinite both;
    /* Reason: GPU Acceleration for scale performance */
    will-change: opacity;
  }

  span:nth-of-type(2) {
    animation-delay: 0.2s;
  }

  span:nth-of-type(3) {
    animation-delay: 0.4s;
  }
`;

/**
 * Senior Optimization: High-scale List Item Wrapper.
 *
 * Reason: Using content-visibility: auto natively tells the browser
 * to skip the rendering and animation cycles of off-screen cards.
 * This allows even thousands of cards with active animations to
 * exist without impacting battery life or scroll performance.
 */
const StyledOptimizedCardWrapper = styled.div`
  /* Removed content-visibility to prevent white-box flicker on fast scroll */
`;
